import { Button, Flex, Form, Input, InputNumber, Select } from "antd";
import { useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  useCreateLectureMutation,
  useGetLectureByIdQuery,
  useUpdateLectureMutation,
} from "../../../../app/features/adminContent/adminContent.api";
import type {
  ContentType,
  LectureDto,
  UpdateLectureDto,
} from "../../../../app/features/adminContent/adminContent.types";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import FileUploader from "../../../../components/FileUploader";
import { ENV_CONFIG } from "../../../../config/global.config";
import { toast } from "../../../../lib/toast";

const YOUTUBE_REGEX = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;

interface LectureFormProps {
  sectionId: number;
  courseId: number;
  onClose: () => void;
}

export default function LectureForm({
  sectionId,
  courseId,
  onClose,
  lectureId,
}: LectureFormProps & { lectureId?: number }) {
  const [form] = Form.useForm();
  const contentType = Form.useWatch("contentType", form);
  const [createLecture, { isLoading: isCreating }] = useCreateLectureMutation();
  const [updateLecture, { isLoading: isUpdating }] = useUpdateLectureMutation();
  const isEditMode = !!lectureId;

  const { data: lectureDataResponse, isLoading: isFetching } =
    useGetLectureByIdQuery(lectureId!, {
      skip: !isEditMode,
    });

  const lectureData = lectureDataResponse;

  useEffect(() => {
    if (isEditMode && lectureData) {
      form.setFieldsValue({
        title: lectureData.title,
        contentType: lectureData.contentType,
        contentUrl:
          lectureData.contentType === "PDF" ||
          lectureData.contentType === "EMBED"
            ? `${ENV_CONFIG.IMAGE_URL}/${lectureData.content?.url}`
            : lectureData.content?.url || "",
        articleHtml: lectureData.content?.html || "", // Assuming article content is also in url for now, adjusted based on typical structure if separate
        durationSec: lectureData.durationSec,
        sortOrder: lectureData.sortOrder,
      });
      // Handle articleHtml separately if backend response structure differs
      if (lectureData.contentType === "ARTICLE") {
        form.setFieldValue("articleHtml", lectureData.content?.html);
      }
    }
  }, [isEditMode, lectureData, form]);

  const handleSubmit = async (values: any) => {
    try {
      let contentUrl = values.contentUrl;

      // Ensure IMAGE_URL is present for PDF/EMBED
      if (
        (values.contentType === "PDF" || values.contentType === "EMBED") &&
        contentUrl &&
        ENV_CONFIG.IMAGE_URL
      ) {
        if (!contentUrl.startsWith(ENV_CONFIG.IMAGE_URL)) {
          // Removes leading slash from contentUrl if present to avoid double slashes
          const cleanPath = contentUrl.startsWith("/")
            ? contentUrl.substring(1)
            : contentUrl;
          contentUrl = `${ENV_CONFIG.IMAGE_URL}/${cleanPath}`;
        }
      }

      if (isEditMode && lectureId) {
        const updateData: UpdateLectureDto = {
          title: values.title,
          contentType: values.contentType as ContentType,
          contentUrl: contentUrl,
          articleHtml: values.articleHtml,
          durationSec: values.durationSec || 0,
          isPreview: false,
          sortOrder: values.sortOrder,
          sectionId,
        };
        await updateLecture({ id: lectureId, body: updateData }).unwrap();
        toast("success", "Lecture updated successfully");
      } else {
        const createData: LectureDto = {
          courseId,
          sectionId,
          title: values.title,
          contentType: values.contentType as ContentType,
          contentUrl: contentUrl,
          articleHtml: values.articleHtml,
          durationSec: values.durationSec || 0,
          isPreview: false,
          sortOrder: values.sortOrder,
        };
        await createLecture(createData).unwrap();
        toast("success", "Lecture created successfully");
      }

      form.resetFields();
      onClose();
    } catch (error: any) {
      toast(
        "error",
        error?.data?.message ||
          `Failed to ${isEditMode ? "update" : "create"} lecture`,
      );
    }
  };

  const isLoading = isCreating || isUpdating || isFetching;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      className="custom-form"
      size="large"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
        <Form.Item
          name="title"
          label="Lecture Title"
          rules={[{ required: true, message: "Please enter lecture title" }]}
          className="col-span-1 md:col-span-2"
        >
          <Input placeholder="Enter lecture title" />
        </Form.Item>

        <Form.Item
          name="contentType"
          label="Type"
          rules={[{ required: true, message: "Please select type" }]}
          className="col-span-1"
        >
          <Select
            placeholder="Select"
            options={types}
            suffixIcon={<SelectDownIcon />}
            allowClear
            onChange={() => {
              form.setFieldValue("contentUrl", undefined);
              form.setFieldValue("durationSec", undefined);
            }}
            // disabled={isEditMode} // Usually type isn't editable, enable if needed
          />
        </Form.Item>
        <Form.Item
          name="sortOrder"
          label="Sorting Order"
          rules={[{ required: true, message: "Please enter sorting order" }]}
          className="md:col-span-3"
        >
          <InputNumber className="w-full" placeholder="Type Order" />
        </Form.Item>

        {/* Dynamic Fields */}
        <>
          {contentType === "VIDEO" && (
            <>
              <Form.Item
                name="contentUrl"
                label="Video URL (YouTube)"
                className="col-span-1 md:col-span-2"
                normalize={(value) => value?.trim()}
                rules={[
                  { required: true, message: "Please enter video URL" },
                  {
                    pattern: YOUTUBE_REGEX,
                    message: "Please enter a valid YouTube URL",
                  },
                ]}
              >
                <Input placeholder="Enter YouTube video URL" />
              </Form.Item>
              <Form.Item
                name="durationSec"
                label="Video Duration (Sec)"
                rules={[
                  { required: true, message: "Please enter video duration" },
                ]}
              >
                <InputNumber placeholder="Enter video duration" />
              </Form.Item>
            </>
          )}

          {contentType === "ARTICLE" && (
            <Form.Item
              name="articleHtml"
              label="Article Content"
              className="md:col-span-3"
              rules={[
                { required: true, message: "Please enter article content" },
              ]}
            >
              <ReactQuill
                theme="snow"
                placeholder="Write your article here..."
                className="h-32 pb-4 mb-4"
              />
            </Form.Item>
          )}

          {contentType === "PDF" && (
            <FileUploader
              form={form}
              name="contentUrl"
              accept=".pdf"
              className="md:col-span-3"
              label="Upload PDF"
              required
              buttonText="Upload PDF"
              title="Upload PDF"
              maxSize={10}
            />
          )}

          {contentType === "EMBED" && (
            <FileUploader
              form={form}
              name="contentUrl"
              accept=".doc,.docx,.ppt,.pptx,.pdf"
              className="md:col-span-3"
              label="Upload Document"
              required
              buttonText="Upload Document"
              title="Upload Document"
              maxSize={10}
            />
          )}
        </>
      </div>

      <Flex justify="end" gap={16} className="mt-4">
        <Button htmlType="button" disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <Button htmlType="submit" type="primary" loading={isLoading}>
          {isEditMode ? "Update Lecture" : "Create Lecture"}
        </Button>
      </Flex>
    </Form>
  );
}

const types = [
  {
    label: "Video",
    value: "VIDEO",
  },
  {
    label: "Article",
    value: "ARTICLE",
  },
  {
    label: "PDF",
    value: "PDF",
  },
  {
    label: "Embed",
    value: "EMBED",
  },
];
